import _ from 'lodash';
import { adapi, Debug, storeGetters, permissions, moment, CampaignTargetPopup } from '~';
import { mapGetters, mapState, mapActions } from 'vuex';
import { FLOWS, CAMPAIGN_TYPES, MODES, STATUSES } from '@/util/constants';
import Status from '@/components/campaign-status/CampaignStatus.vue';
import FacebookPixel from '@/components/facebook-pixel/FacebookPixel.vue';
import BoxWidget from '@/components/box-widget/BoxWidget.vue';

const debug = Debug('campaign-info');

const createField = (name, caption, dataFn, filterFn = () => true) => ({
  name,
  caption,
  data: dataFn,
  filterFn,
});

const availableFields = [
  createField('target.locations', 'campaign_field.title.area',
    (c, t) => c.target.locations
      .map(l => t('campaign_field.format.location', { name: l.name, type: l.type }))
      .join(', '),
    c => c.type !== CAMPAIGN_TYPES.EXTERNAL,
  ),
  createField('target.radius', 'campaign_field.title.radius',
    (c, t) => t('campaign_field.format.radius', {
      radius: c.target.radius,
      unit: c.target.distance_unit,
    }),
    c => c.type !== CAMPAIGN_TYPES.EXTERNAL,
  ),
  createField('target.age-gender', 'campaign_field.title.age-gender',
    (c, t) => t('campaign_field.format.gender-and-age', {
      from: c.target.ages.from,
      to: c.target.ages.to,
      genders: c.target.genders,
    }),
    c => c.type !== CAMPAIGN_TYPES.EXTERNAL,
  ),
  createField('target.interests', 'campaign_field.title.interests',
    (c, t, box) => box.interests
      .filter(i => c.target.interests.indexOf(i.id) !== -1)
      .map(i => {
        const key = `terms.interests.${i.slug}.name`;
        if (key === t(key)) {
          return i.name;
        }
        return t(key);
      })
      .join(', '),
    c => c.type !== CAMPAIGN_TYPES.EXTERNAL,
  ),
  createField('target.language', 'campaign_field.title.language',
    (c, t) => t('fn.complex.language', { code: _.get(c.target, 'language') }),
  ),
  createField('target.placement', 'campaign_field.title.placement',
    (c, t) => t(`terms.placement.facebook${c.target.use_instagram ? '-instagram' : ''}`),
  ),
  createField('reach.potential', 'campaign_field.title.potential-reach',
    (c, t) => t('campaign_field.format.potential-reach', { reach: c.potential_reach }),
    c => c.type !== CAMPAIGN_TYPES.EXTERNAL,
  ),
  createField('reach.guaranteed', 'campaign_field.title.guaranteed-reach',
    (c, t) => t('campaign_field.format.potential-reach', { reach: _.get(c, 'campaign_rule.min_reach', 0) }),
    c => c.type !== CAMPAIGN_TYPES.EXTERNAL,
  ),
  createField('package', 'campaign_field.title.package',
    (c) => _.get(c, 'campaign_rule.package', null),
  ),
  createField('format', 'campaign_field.title.format',
    (c, t) => t('campaign_field.format.format', { format: c.type }),
  ),
  createField('initial-start-date', 'campaign_field.title.initial-start-date',
    (c, t) => {
      if (_.get(c, 'baseStatus.flow_type', null) === FLOWS.ASAP) {
        return t(`terms.flows.${FLOWS.ASAP}`);
      } else if (c.start_date) {
        return t('campaign_field.format.start-date', { startDate: c.start_date });
      }
      return '-';
    },
  ),
  createField('campaign.name', 'campaign_field.title.name',
    (c) => c.name,
  ),
  createField('campaign.code', 'campaign_field.title.code',
    (c) => c.code,
  ),
  createField('landing-page', 'campaign_field.title.landing-page',
    (c) => _.get(c, 'landingpages', []).map(p => p.link).join(', '),
  ),
  createField('facebook-page', 'campaign_field.title.facebook-page',
    (c) => _.get(c, 'fbpage.link', '-'),
  ),
  createField('days-in-status', 'campaign_field.title.days-in-status',
    (c) => _.get(c, 'days_in_current_status', '-').toString(),
  ),
  createField('lead-data', 'campaign_field.title.lead-data',
    (c, t, box) => {
      const data = _.get(box.config, 'goal_types.lead_generation.config.user_data', []);
      if (_.isArray(data)) {
        return data.join(', ');
      }
      return '-';
    },
  ),
  createField('campaign.creator_email', 'campaign_field.title.creator',
    (c) => c.creator_email,
  ),
  createField('objective', 'campaign_field.title.objective',
    (c, t) => (!_.isEmpty(c.objective) ? t(`terms.objective-texts.${c.objective}.name`) : '-'),
  ),
  createField('campaign_iteration', 'campaign_field.title.campaign_iteration',
    (c) => {
      const iterations = _.get(c, 'iterations', []);
      let lastIterId = '-';
      let lastTime = null;
      iterations.forEach(iteration => {
        const curIterStartTime = moment(iteration.start_date);
        if (lastTime === null || lastTime.isBefore(curIterStartTime)) {
          lastIterId = iteration.remote_id;
          lastTime = curIterStartTime;
        }
      });
      return lastIterId.toString();
    },
    c => c.mode === MODES.subscription,
  ),
  createField('goal_type', 'campaign_field.title.goal_type',
    (c, t) => t('fn.complex.goal_type', { goal_type: _.get(c, 'campaign_rule.goal_type') }),
  ),
  createField('campaign.clone_of', 'campaign_field.title.clone_of',
    (c) => c.clone_of,
    (c) => c.clone_of !== null,
  ),
  createField('campaign.clone_for', 'campaign_field.title.clone_for',
    (c) => c.clone_for,
    (c) => c.clone_for !== null,
  ),
  createField('status', 'campaign_field.title.status',
    (c, t) => {
      let code = _.get(c, 'baseStatus.code', null);

      if (code === STATUSES.ONL && _.get(c, 'issues', []).filter(i => !i.finished_at).length) {
        code = `${code}-issues`;
      }

      return t(`terms.statuses.${code}`);
    },
  ),
  createField('duplicate_of', 'campaign_field.title.duplicate_of',
    c => {
      const detail = _.get(c, 'details', [])
        .find(d => d.name === 'duplicate_of_draft_campaign_code');

      return detail ? detail.value : null;
    },
    c => {
      const detail = _.get(c, 'details', [])
        .find(d => d.name === 'duplicate_of_draft_campaign_code');

      return detail && detail.value;
    },
  ),
  createField('facebook_pixel', 'campaign_field.title.facebook_pixel',
    c => _.get(c, 'facebook_pixel'),
    c => _.get(c, 'facebook_pixel.pixel_id'),
  ),
];

export default {
  props: {
    fields: {
      type: Array,
      default: () => [
        'target.locations',
        'target.radius',
        'target.age-gender',
        'target.interests',
        'target.language',
        'target.placement',
        'reach.potential',
        'reach.guaranteed',
        'package',
        'format',
        'initial-start-date',
        'status',
        'days-in-status',
        'campaign.creator_email',
        'objective',
        'campaign_iteration',
        'goal_type',
        'campaign.clone_of',
        'campaign.clone_for',
        'duplicate_of',
        'facebook_pixel',
      ],
    },
    showStatus: {
      type: Boolean,
      default: true,
    },
    headerCampaignOpen: {
      type: Boolean,
      default: false,
    },
    showCreatorEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      newCreatorEmail: null,
      loading: false,
    };
  },
  components: {
    FacebookPixel,
    'campaign-status': Status,
    'box-widget': BoxWidget,
    'campaign-target-popup': CampaignTargetPopup,
  },
  computed: {
    ...mapGetters('campaign', { campaign: 'currentCampaign' }),
    ...mapGetters('interests', ['interests']),
    ...mapState('genericConfig', ['config']),
    ...mapState('auth', ['user']),
    canEditCreator() {
      return this.showCreatorEdit && permissions.canGlobal(this.user, 'Campaign', ['changeCreator']);
    },
    permissions() {
      return permissions.getPermissions(this.campaign);
    },
    canMoveCampaign() {
      return this.permissions.includes('moveToCap');
    },
    visibleFields() {
      return this.fields
        .map(name => availableFields.find(f => f.name === name))
        .filter(f => !!f)
        .filter(f => f.filterFn(this.campaign))
        .filter(f => !(f.name === 'campaign.creator_email' && this.canEditCreator))
        .map(f => ({
          slug: f.name,
          name: this.$t(f.caption),
          value: f.data(this.campaign, this.$t.bind(this), this),
        }));
    },
    changedTarget() {
      let targetConf = _.get(this.campaign, 'pending_target', null);
      if (!targetConf) {
        targetConf = _.get(this.campaign, 'last_target', null);
      }
      return targetConf;
    },
  },
  methods: {
    ...mapActions('campaign', ['reloadCampaign']),
    isEmpty(value) {
      return _.isEmpty(value) ? '-' : value;
    },
    openCreatorModal() {
      this.newCreatorEmail = '';
      this.$refs.modal.show();
    },
    openTargetChangeModal() {
      this.$refs.targetChangeModal.show();
    },
    changeCreator() {
      this.loading = true;
      const newEmailValue = this.newCreatorEmail;

      adapi.post(`campaigns/${storeGetters.getRouterParams().code}/change-creator`, null, {
        email: newEmailValue,
      })
        .then(result => {
          debug.info('success', result);
          this.$toast.success({ message: this.$t('pos.campaign.change-creator-modal.updated') });
          this.$refs.modal.hide();
          this.loading = false;
        })
        .then(() => this.reloadCampaign())
        .catch(error => {
          this.$toast.error({ message: adapi.translateAdapiErrors(error.errors, this.$t) });
          this.loading = false;
        });
    },
    cancel() {
      this.$refs.modal.hide();
    },
    getFieldByName(fieldName) {
      const field = availableFields.find(f => f.name === fieldName);
      return {
        slug: fieldName,
        name: this.$t(field.caption),
        value: field.data(this.campaign, this.$t.bind(this), this),
      };
    },
    forceMoveCampaign() {
      adapi.post(`campaigns/${this.campaign.code}/move`, { status: 'CAP' }, null)
        .then(result => {
          debug.info('success', result);
          this.$toast.success({ message: this.$t('pos.campaign.moved') });
        })
        .then(() => this.reloadCampaign())
        .catch(error => {
          this.$toast.error({ message: adapi.translateAdapiErrors(error.errors, this.$t) });
        });
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/campaign-info/CampaignInfo.js